import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bell, Mail, Calendar, UserCheck, MessageCircle, AtSign, Clock, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { API } from "@/utils/constants";
import { PushNotificationToggle } from "@/components/PushNotificationToggle";

export const NotificationSettingsView = () => {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [digestPreview, setDigestPreview] = useState(null);
  const [sendingDigest, setSendingDigest] = useState(false);

  useEffect(() => {
    const fetchPrefs = async () => {
      try {
        const r = await axios.get(`${API}/notification-preferences`);
        setPrefs(r.data);
      } catch { toast.error("Failed to load preferences"); }
      setLoading(false);
    };
    fetchPrefs();
  }, []);

  const updatePref = async (key, value) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/notification-preferences`, { [key]: value });
      setPrefs(r.data);
      toast.success("Preference updated");
    } catch { toast.error("Failed to update"); }
    setSaving(false);
  };

  const loadDigestPreview = async () => {
    try {
      const r = await axios.get(`${API}/notifications/digest-preview`);
      setDigestPreview(r.data);
    } catch { toast.error("Failed to load preview"); }
  };

  const sendDigestNow = async () => {
    setSendingDigest(true);
    try {
      const r = await axios.post(`${API}/notifications/send-digest`);
      if (r.data.status === "digest_disabled") {
        toast.info("Enable digest frequency first");
      } else {
        toast.success("Digest sent!");
      }
    } catch { toast.error("Failed to send digest"); }
    setSendingDigest(false);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const notifOptions = [
    { key: "email_on_assignment", label: "Task Assignments", desc: "When someone assigns you a task", icon: UserCheck, color: "text-blue-500" },
    { key: "email_on_status_change", label: "Status Changes", desc: "When a task you're assigned to moves columns", icon: Clock, color: "text-purple-500" },
    { key: "email_on_due_date_reminder", label: "Due Date Reminders", desc: "Reminders for tasks due today or tomorrow", icon: Calendar, color: "text-amber-500" },
    { key: "email_on_mention", label: "Mentions", desc: "When someone @mentions you in a comment", icon: AtSign, color: "text-pink-500" },
    { key: "email_on_comment", label: "New Comments", desc: "When someone comments on your task", icon: MessageCircle, color: "text-emerald-500" },
  ];

  return (
    <div className="p-6 max-w-2xl">
      <h2 className="text-xl font-bold mb-1 flex items-center gap-2" data-testid="notification-settings-title">
        <Bell className="h-5 w-5 text-primary" />Notification Settings
      </h2>
      <p className="text-sm text-muted-foreground mb-6">Configure how and when you receive email notifications</p>

      <div className="space-y-6">
        <PushNotificationToggle />
        <div className="bg-card rounded-xl border p-5">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Mail className="h-4 w-4 text-primary" />Email Notifications
          </h3>
          <div className="space-y-4">
            {notifOptions.map(opt => (
              <div key={opt.key} className="flex items-center justify-between py-2" data-testid={`pref-${opt.key}`}>
                <div className="flex items-center gap-3">
                  <opt.icon className={`h-5 w-5 ${opt.color}`} />
                  <div>
                    <div className="text-sm font-medium">{opt.label}</div>
                    <div className="text-xs text-muted-foreground">{opt.desc}</div>
                  </div>
                </div>
                <Switch
                  checked={prefs?.[opt.key] ?? true}
                  onCheckedChange={(v) => updatePref(opt.key, v)}
                  disabled={saving}
                  data-testid={`switch-${opt.key}`}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="bg-card rounded-xl border p-5">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Send className="h-4 w-4 text-primary" />Email Digest
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Receive a summary of your tasks, deadlines, and updates on a regular schedule.
          </p>
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-sm font-medium">Digest Frequency</div>
              <div className="text-xs text-muted-foreground">How often to receive your digest email</div>
            </div>
            <Select
              value={prefs?.digest_frequency || "off"}
              onValueChange={(v) => updatePref("digest_frequency", v)}
              data-testid="digest-frequency-select"
            >
              <SelectTrigger className="w-32" data-testid="digest-frequency-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="off">Off</SelectItem>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={loadDigestPreview} data-testid="preview-digest-btn">
              Preview Digest
            </Button>
            <Button size="sm" onClick={sendDigestNow} disabled={sendingDigest || prefs?.digest_frequency === "off"} data-testid="send-digest-btn">
              {sendingDigest ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}
              Send Now
            </Button>
          </div>

          {digestPreview && (
            <div className="mt-4 p-4 bg-muted/50 rounded-lg space-y-3" data-testid="digest-preview">
              <h4 className="font-medium text-sm">Digest Preview</h4>
              {digestPreview.tasks_overdue?.length > 0 && (
                <div>
                  <Badge variant="destructive" className="mb-1">Overdue ({digestPreview.tasks_overdue.length})</Badge>
                  <div className="space-y-1">{digestPreview.tasks_overdue.map((t, i) => (
                    <div key={i} className="text-sm text-red-600 dark:text-red-400">{t.title} — due {t.due_date}</div>
                  ))}</div>
                </div>
              )}
              {digestPreview.tasks_due_soon?.length > 0 && (
                <div>
                  <Badge className="mb-1 bg-amber-500">Due Soon ({digestPreview.tasks_due_soon.length})</Badge>
                  <div className="space-y-1">{digestPreview.tasks_due_soon.map((t, i) => (
                    <div key={i} className="text-sm text-amber-700 dark:text-amber-400">{t.title} — {t.due_date}</div>
                  ))}</div>
                </div>
              )}
              {digestPreview.tasks_completed?.length > 0 && (
                <div>
                  <Badge className="mb-1 bg-emerald-500">Completed ({digestPreview.tasks_completed.length})</Badge>
                  <div className="space-y-1">{digestPreview.tasks_completed.map((t, i) => (
                    <div key={i} className="text-sm text-emerald-600 dark:text-emerald-400">{t.title}</div>
                  ))}</div>
                </div>
              )}
              {!digestPreview.tasks_overdue?.length && !digestPreview.tasks_due_soon?.length && !digestPreview.tasks_completed?.length && (
                <p className="text-sm text-muted-foreground">No notable updates right now.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
